import { error, ValidationError } from 'common/errors';
import {
  pushErrors,
  pushInitiateIdentityValidationRequest,
  pushInitiateIdentityValidationResponse
} from 'common/gamp';
import { saveUser } from 'communicator/store';
/* START GENAI@GHCOPILOT */
import {
  getAccessToken,
  getApiKey,
  getDcfExperience,
  getIdentityType,
  getIsFeatureToggleEnabled,
  getIsVdiDisabled,
  getMeta,
  getUserCountry,
  getUserEmail,
  getUserLocale,
  getUserPhone
} from 'communicator/store/selectors';
/* END GENAI@GHCOPILOT */
import { fetchOrchReissuance } from 'communicator/utils/api/reissuance';
import { getHeader } from 'communicator/utils/fetch';
import { getOrchestratorLockoutFlow } from 'communicator/utils/get-lockout-flow';
import { markEnd, markStart } from 'communicator/utils/instrumentation';
import parseExpiryFromAccessToken from 'communicator/utils/parse-expiry-from-token';
import { FetchReissuanceOrchOptions } from 'types/api/reissuance';
import { InitiateIdentityValidationPayload } from 'types/visa-sdk/initiate-identity-validation';
import initiateIdentityValidationLegacy from './initiate-identity-validation-legacy';

export default async function initiateIdentityValidation(): Promise<
  InitiateIdentityValidationPayload | ValidationError
> {
  try {
    if (!getIsFeatureToggleEnabled('orchestratorReissuanceApi')) {
      markStart('INITIATE_IDENTITY_VALIDATION_LEGACY');
      return initiateIdentityValidationLegacy();
    }

    markStart('INITIATE_IDENTITY_VALIDATION');
    pushInitiateIdentityValidationRequest();
    const stime = Date.now();
    const accessToken = getAccessToken();
    const email = getUserEmail();
    const identityType = getIdentityType();
    const phone = getUserPhone();

    // If initiateIdentityValidation is called without an accessToken, e.g. when
    // idLookup returns NOTPRESENT, return an AUTH_ERROR. If email or mobile is not present,
    // idLookup was either skipped or failed.
    if (!accessToken?.token || !(email || phone) || !identityType) {
      markEnd('INITIATE_IDENTITY_VALIDATION', {
        doFlush: true,
        failure: true,
        reason: 'AUTH_ERROR'
      });

      // START GENAI@GHCOPILOT
      pushErrors('INITIATE_IDENTITY_VALIDATION', 'AUTH_ERROR');
      // END GENAI@GHCOPILOT

      return error('AUTH_ERROR');
    }

    const orchReissuanceRequestOptions: FetchReissuanceOrchOptions = {
      accessToken: accessToken.token,
      apikey: getApiKey(),
      identityType,
      identityValue: identityType === 'EMAIL_ADDRESS' ? (email ?? '') : (phone ?? ''),
      isVdiDisabled: getIsVdiDisabled()
    };

    if (getIsFeatureToggleEnabled('fidoAuthEnabled') && getDcfExperience() === 'integrated') {
      orchReissuanceRequestOptions.isBiometricsEnabledDevice = getMeta().isBiometricsEnabledDevice;
    }

    const reissuanceResponse = await fetchOrchReissuance(orchReissuanceRequestOptions);

    if (!reissuanceResponse.ok) {
      if (getOrchestratorLockoutFlow(reissuanceResponse)) {
        saveUser({
          accessToken: null
        });

        markEnd('INITIATE_IDENTITY_VALIDATION', {
          doFlush: true,
          failure: true,
          reason: 'ACCT_INACCESSIBLE'
        });

        // START GENAI@GHCOPILOT
        pushErrors('INITIATE_IDENTITY_VALIDATION', 'ACCT_INACCESSIBLE');
        // END GENAI@GHCOPILOT

        return error('ACCT_INACCESSIBLE');
      }

      markEnd('INITIATE_IDENTITY_VALIDATION', {
        doFlush: true,
        failure: true,
        reason: 'OTP_SEND_FAILED'
      });

      // START GENAI@GHCOPILOT
      pushErrors('INITIATE_IDENTITY_VALIDATION', 'OTP_SEND_FAILED');
      // END GENAI@GHCOPILOT

      return error('OTP_SEND_FAILED');
    }

    const newAccessToken = getHeader(reissuanceResponse, 'access_token') as string;
    const expiresIn = parseExpiryFromAccessToken(newAccessToken) as number;

    saveUser({
      accessToken: {
        expiresIn,
        isPartial: true,
        token: newAccessToken
      },
      idValidationSessionId: reissuanceResponse.data.idValidationSessionId
    });

    pushInitiateIdentityValidationResponse(Date.now() - stime);

    markEnd('INITIATE_IDENTITY_VALIDATION');
    return {
      maskedValidationChannel:
        reissuanceResponse.data.maskedValidationChannel.maskedValidationChannel,
      profileCountry: getUserCountry(),
      profileLocale: getUserLocale()
    };
  } catch (err: unknown) {
    // START GENAI@GHCOPILOT
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    const sErr = JSON.stringify(err) !== undefined ? JSON.stringify(err) : 'Unknown error';
    markEnd('INITIATE_IDENTITY_VALIDATION', { doFlush: true, failure: true, reason: sErr });
    pushErrors('Initiate Identity Validation', err);
    return error('INITIATE_IDENTITY_VALIDATION_FAILED');
    // END GENAI@GHCOPILOT
  }
}
